/**
 * Caption shown below a card's data while a browser translation is active, naming
 * what was translated and the languages. Also renders the inline error variant
 * when a translation attempt fails and the original stays visible.
 */
import { useTranslation } from "@refinedev/core";

import { Typography } from "@mui/material";

import { DEFAULT_LOCALE } from "@/utils/i18n-locale";

interface TranslationCaptionProps {
  /** What was translated, already resolved to a UI string (e.g. "Condition names"). */
  subject: string;
  /** Target language name in the current UI language (e.g. "French"). */
  targetLanguageLabel: string;
  /** True to render the translation-failure message instead of the caption. */
  error?: boolean;
  /** Test id for the rendered caption. */
  "data-testid"?: string;
}

/**
 * Renders the translation caption, or the failure message when `error` is set.
 *
 * @param props - The subject, target language label, and error flag.
 * @returns The caption element.
 */
export const TranslationCaption = ({
  subject,
  targetLanguageLabel,
  error = false,
  "data-testid": dataTestId,
}: TranslationCaptionProps) => {
  const { translate } = useTranslation();

  if (error) {
    return (
      <Typography variant="caption" color="error" data-testid={dataTestId}>
        {translate("freeTextTranslation.error")}
      </Typography>
    );
  }

  return (
    <Typography variant="caption" color="text.secondary" data-testid={dataTestId}>
      {translate("freeTextTranslation.caption", {
        subject,
        source: translate(`languageNames.${DEFAULT_LOCALE}`),
        target: targetLanguageLabel,
      })}
    </Typography>
  );
};
